import { useState } from 'react';
import toast from 'react-hot-toast';

interface UseDeleteConfirmationProps<T> {
    items: T[];
    setItems: (items: T[]) => void;
    deleteFunction: (id: string) => Promise<void>;
    getItemName: (item: T) => string;
    getItemId: (item: T) => string;
    itemType: string;
}

export const useDeleteConfirmation = <T>({
    items,
    setItems,
    deleteFunction,
    getItemName,
    getItemId,
    itemType
}: UseDeleteConfirmationProps<T>) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<T | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteClick = (item: T) => {
        setItemToDelete(item);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;

        setIsDeleting(true);
        try {
            const itemId = getItemId(itemToDelete);
            const itemName = getItemName(itemToDelete);

            await deleteFunction(itemId);
            setItems(items.filter(item => getItemId(item) !== itemId));
            toast.success(`${itemType} "${itemName}" deleted successfully`);
            setShowDeleteModal(false);
            setItemToDelete(null);
        } catch (err: any) {
            console.error(`Error deleting ${itemType.toLowerCase()}:`, err);
            const errorMessage = err.response?.data?.message || `Failed to delete ${itemType.toLowerCase()}. Please try again.`;
            toast.error(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        if (!isDeleting) {
            setShowDeleteModal(false);
            setItemToDelete(null);
        }
    };

    return {
        showDeleteModal,
        itemToDelete,
        isDeleting,
        handleDeleteClick,
        handleDeleteConfirm,
        handleDeleteCancel,
        getItemName: itemToDelete ? getItemName(itemToDelete) : ''
    };
};
